import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { ScrollArea } from '../ui/scroll-area';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '../ui/command';
import { cn } from '../../lib/utils';
import { toast } from 'sonner';
import {
  Book, Link2, Plus, X, ExternalLink, Search,
  FileText, Users, Sparkles, Crown, Gem, MapPin,
  Calendar, Scroll, Shield, BookOpen, Skull, Star,
  ChevronRight
} from 'lucide-react';

const API_URL = '/api';

// Article type icons
const ARTICLE_TYPE_ICONS = {
  free: FileText,
  faction: Users,
  deity: Sparkles,
  historical_figure: Crown,
  artifact: Gem,
  location: MapPin,
  event: Calendar,
  spell: Scroll,
  organization: Shield,
  concept: BookOpen,
  creature: Skull,
  legend: Star
};

const ARTICLE_TYPE_LABELS = {
  free: { fr: 'Libre', en: 'Free' },
  faction: { fr: 'Faction', en: 'Faction' },
  deity: { fr: 'Divinité', en: 'Deity' },
  historical_figure: { fr: 'Figure historique', en: 'Historical Figure' },
  artifact: { fr: 'Artefact', en: 'Artifact' },
  location: { fr: 'Lieu', en: 'Location' },
  event: { fr: 'Événement', en: 'Event' },
  spell: { fr: 'Sort', en: 'Spell' },
  organization: { fr: 'Organisation', en: 'Organization' },
  concept: { fr: 'Concept', en: 'Concept' },
  creature: { fr: 'Créature', en: 'Creature' },
  legend: { fr: 'Légende', en: 'Legend' }
};

const LINK_TYPES = {
  related_to: { fr: 'Lié à', en: 'Related to' },
  mentions: { fr: 'Mentionne', en: 'Mentions' },
  part_of: { fr: 'Fait partie de', en: 'Part of' },
  created_by: { fr: 'Créé par', en: 'Created by' },
  located_in: { fr: 'Situé dans', en: 'Located in' },
  member_of: { fr: 'Membre de', en: 'Member of' },
  enemy_of: { fr: 'Ennemi de', en: 'Enemy of' },
  ally_of: { fr: 'Allié de', en: 'Ally of' }
};

export function LoreLinksPanel({
  entityType,  // "npc", "quest", "location", "pin", "session", "treasure", "encounter"
  entityId,
  entityName,
  campaignId,
  isMJ = false,
  compact = false  // For inline display in cards
}) {
  const { language } = useAuth();
  const navigate = useNavigate();
  
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [availableArticles, setAvailableArticles] = useState([]);
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [selectedLinkType, setSelectedLinkType] = useState('related_to');
  
  // New article form
  const [newArticle, setNewArticle] = useState({
    title: '',
    article_type: 'free',
    summary: ''
  });

  // Fetch existing links
  const fetchLinks = useCallback(async () => {
    if (!entityId) return;
    
    try {
      const response = await axios.get(`${API_URL}/entities/${entityType}/${entityId}/lore-links`);
      setLinks(response.data);
    } catch (error) {
      console.error('Failed to fetch lore links');
    } finally {
      setLoading(false);
    }
  }, [entityType, entityId]);

  // Fetch available articles for linking
  const fetchArticles = useCallback(async () => {
    if (!campaignId) return;
    
    try {
      const response = await axios.get(`${API_URL}/campaigns/${campaignId}/lore`);
      setAvailableArticles(response.data);
    } catch (error) {
      console.error('Failed to fetch articles');
    }
  }, [campaignId]);

  useEffect(() => {
    fetchLinks();
  }, [fetchLinks]);

  useEffect(() => {
    if (linkDialogOpen) {
      fetchArticles();
    }
  }, [linkDialogOpen, fetchArticles]);

  // Create link
  const handleCreateLink = async () => {
    if (!selectedArticle) return;
    
    try {
      await axios.post(`${API_URL}/lore-links`, {
        source_type: entityType,
        source_id: entityId,
        target_type: 'lore',
        target_id: selectedArticle.id,
        link_type: selectedLinkType
      });
      
      await fetchLinks();
      setLinkDialogOpen(false);
      setSelectedArticle(null);
      setSelectedLinkType('related_to');
      toast.success(language === 'fr' ? 'Lien créé !' : 'Link created!');
    } catch (error) {
      toast.error(language === 'fr' ? 'Erreur' : 'Error');
    }
  };

  // Delete link
  const handleDeleteLink = async (linkId) => {
    try {
      await axios.delete(`${API_URL}/lore-links/${linkId}`);
      setLinks(prev => prev.filter(l => l.link_id !== linkId));
      toast.success(language === 'fr' ? 'Lien supprimé' : 'Link removed');
    } catch (error) {
      toast.error(language === 'fr' ? 'Erreur' : 'Error');
    }
  };

  // Create new article and link it
  const handleCreateAndLink = async () => {
    if (!newArticle.title.trim()) return;
    
    try {
      // Create article
      const articleResponse = await axios.post(`${API_URL}/campaigns/${campaignId}/lore`, {
        ...newArticle,
        tags: [],
        visibility: 'players',
        revelation_level: 'known'
      });
      
      // Create link
      await axios.post(`${API_URL}/lore-links`, {
        source_type: entityType,
        source_id: entityId,
        target_type: 'lore',
        target_id: articleResponse.data.id,
        link_type: selectedLinkType
      });
      
      await fetchLinks();
      setCreateDialogOpen(false);
      setNewArticle({ title: '', article_type: 'free', summary: '' });
      toast.success(language === 'fr' ? 'Article créé et lié !' : 'Article created and linked!');
    } catch (error) {
      toast.error(language === 'fr' ? 'Erreur' : 'Error');
    }
  };

  // Navigate to article
  const handleOpenArticle = (articleId) => {
    navigate(`/campaigns/${campaignId}/lore?article=${articleId}`);
  };

  // Filter articles based on search
  const filteredArticles = availableArticles.filter(article => 
    article.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    article.tags?.some(t => t.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  // Compact mode for inline display
  if (compact) {
    if (links.length === 0 && !isMJ) return null;
    
    return (
      <div className="flex items-center gap-2 flex-wrap">
        {links.length > 0 && (
          <>
            <Book className="h-3 w-3 text-muted-foreground" />
            {links.slice(0, 3).map(link => (
              <Badge 
                key={link.link_id}
                variant="outline"
                className="text-xs cursor-pointer hover:bg-secondary"
                onClick={() => handleOpenArticle(link.id)}
              >
                {link.name}
              </Badge>
            ))}
            {links.length > 3 && (
              <Badge variant="outline" className="text-xs">
                +{links.length - 3}
              </Badge>
            )}
          </>
        )}
        {isMJ && links.length === 0 && (
          <Button 
            variant="ghost" 
            size="sm" 
            className="h-6 text-xs text-muted-foreground"
            onClick={() => setLinkDialogOpen(true)}
          >
            <Link2 className="h-3 w-3 mr-1" />
            {language === 'fr' ? 'Lier au lore' : 'Link to lore'}
          </Button>
        )}
      </div>
    );
  }

  // Full panel mode
  return (
    <div className="border rounded-lg p-4 bg-card/50">
      <div className="flex items-center justify-between mb-3">
        <h4 className="font-medium flex items-center gap-2">
          <Book className="h-4 w-4 text-primary" />
          {language === 'fr' ? 'Lié au Lore' : 'Linked to Lore'}
        </h4>
        {isMJ && (
          <div className="flex items-center gap-1">
            <Dialog open={linkDialogOpen} onOpenChange={setLinkDialogOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" size="sm">
                  <Link2 className="h-3 w-3 mr-1" />
                  {language === 'fr' ? 'Lier' : 'Link'}
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-md">
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    <Link2 className="h-5 w-5" />
                    {language === 'fr' ? 'Lier à un article' : 'Link to article'}
                  </DialogTitle>
                  <DialogDescription>
                    {language === 'fr' 
                      ? `Lier "${entityName}" à un article de l'encyclopédie`
                      : `Link "${entityName}" to an encyclopedia article`}
                  </DialogDescription>
                </DialogHeader>
                
                <div className="space-y-4 py-4">
                  {/* Search articles */}
                  <div>
                    <Label className="text-sm mb-2 block">
                      {language === 'fr' ? 'Rechercher un article' : 'Search article'}
                    </Label>
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder={language === 'fr' ? 'Titre ou tag...' : 'Title or tag...'}
                        className="pl-9"
                      />
                    </div>
                  </div>
                  
                  {/* Article list */}
                  <ScrollArea className="h-48 border rounded-md">
                    <div className="p-2 space-y-1">
                      {filteredArticles.length === 0 ? (
                        <p className="text-sm text-muted-foreground text-center py-4">
                          {language === 'fr' ? 'Aucun article trouvé' : 'No articles found'}
                        </p>
                      ) : (
                        filteredArticles.map(article => {
                          const Icon = ARTICLE_TYPE_ICONS[article.article_type] || FileText;
                          const isSelected = selectedArticle?.id === article.id;
                          
                          return (
                            <div
                              key={article.id}
                              className={cn(
                                "flex items-center gap-2 p-2 rounded cursor-pointer transition-colors",
                                isSelected ? "bg-primary/20 ring-1 ring-primary" : "hover:bg-secondary"
                              )}
                              onClick={() => setSelectedArticle(article)}
                            >
                              <Icon className="h-4 w-4 shrink-0" />
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium truncate">{article.title}</p>
                                <p className="text-xs text-muted-foreground truncate">
                                  {ARTICLE_TYPE_LABELS[article.article_type]?.[language]}
                                </p>
                              </div>
                            </div>
                          );
                        })
                      )}
                    </div>
                  </ScrollArea>
                  
                  {/* Link type */}
                  {selectedArticle && (
                    <div>
                      <Label className="text-sm mb-2 block">
                        {language === 'fr' ? 'Type de lien' : 'Link type'}
                      </Label>
                      <Select value={selectedLinkType} onValueChange={setSelectedLinkType}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {Object.entries(LINK_TYPES).map(([key, labels]) => (
                            <SelectItem key={key} value={key}>
                              {labels[language]}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>
                
                <DialogFooter>
                  <Button variant="outline" onClick={() => setLinkDialogOpen(false)}>
                    {language === 'fr' ? 'Annuler' : 'Cancel'}
                  </Button>
                  <Button onClick={handleCreateLink} disabled={!selectedArticle}>
                    <Link2 className="mr-2 h-4 w-4" />
                    {language === 'fr' ? 'Lier' : 'Link'}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
            
            <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" size="sm">
                  <Plus className="h-3 w-3 mr-1" />
                  {language === 'fr' ? 'Créer' : 'Create'}
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    <Book className="h-5 w-5" />
                    {language === 'fr' ? 'Créer un article lié' : 'Create linked article'}
                  </DialogTitle>
                  <DialogDescription>
                    {language === 'fr'
                      ? `Créer un nouvel article et le lier à "${entityName}"`
                      : `Create a new article and link it to "${entityName}"`}
                  </DialogDescription>
                </DialogHeader>
                
                <div className="space-y-4 py-4">
                  <div>
                    <Label>{language === 'fr' ? 'Type' : 'Type'}</Label>
                    <Select 
                      value={newArticle.article_type}
                      onValueChange={(v) => setNewArticle(prev => ({ ...prev, article_type: v }))}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.entries(ARTICLE_TYPE_LABELS).map(([key, labels]) => {
                          const Icon = ARTICLE_TYPE_ICONS[key];
                          return (
                            <SelectItem key={key} value={key}>
                              <span className="flex items-center gap-2">
                                <Icon className="h-4 w-4" />
                                {labels[language]}
                              </span>
                            </SelectItem>
                          );
                        })}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <Label>{language === 'fr' ? 'Titre' : 'Title'}</Label>
                    <Input
                      value={newArticle.title}
                      onChange={(e) => setNewArticle(prev => ({ ...prev, title: e.target.value }))}
                      placeholder={entityName}
                    />
                  </div>
                  
                  <div>
                    <Label>{language === 'fr' ? 'Résumé' : 'Summary'}</Label>
                    <Input
                      value={newArticle.summary}
                      onChange={(e) => setNewArticle(prev => ({ ...prev, summary: e.target.value }))}
                      placeholder={language === 'fr' ? 'Brève description...' : 'Brief description...'}
                    />
                  </div>
                  
                  <div>
                    <Label>{language === 'fr' ? 'Type de lien' : 'Link type'}</Label>
                    <Select value={selectedLinkType} onValueChange={setSelectedLinkType}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.entries(LINK_TYPES).map(([key, labels]) => (
                          <SelectItem key={key} value={key}>{labels[language]}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                <DialogFooter>
                  <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>
                    {language === 'fr' ? 'Annuler' : 'Cancel'}
                  </Button>
                  <Button onClick={handleCreateAndLink} disabled={!newArticle.title.trim()}>
                    <Plus className="mr-2 h-4 w-4" />
                    {language === 'fr' ? 'Créer et lier' : 'Create & Link'}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        )}
      </div>
      
      {/* Links list */}
      {loading ? (
        <div className="text-sm text-muted-foreground animate-pulse">
          {language === 'fr' ? 'Chargement...' : 'Loading...'}
        </div>
      ) : links.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          {language === 'fr' ? 'Aucun lien vers le lore' : 'No lore links'}
        </p>
      ) : (
        <div className="space-y-2">
          {links.map(link => {
            const Icon = ARTICLE_TYPE_ICONS[link.type] || Book;
            
            return (
              <div 
                key={link.link_id}
                className="flex items-center justify-between p-2 rounded-lg bg-secondary/50 hover:bg-secondary transition-colors group"
              >
                <div 
                  className="flex items-center gap-2 flex-1 min-w-0 cursor-pointer"
                  onClick={() => handleOpenArticle(link.id)}
                >
                  <Icon className="h-4 w-4 text-primary shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{link.name}</p>
                    <p className="text-xs text-muted-foreground">
                      {LINK_TYPES[link.link_type]?.[language] || link.link_type}
                    </p>
                  </div>
                  <ExternalLink className="h-3 w-3 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                </div>
                
                {isMJ && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity"
                    onClick={(e) => { e.stopPropagation(); handleDeleteLink(link.link_id); }}
                  >
                    <X className="h-3 w-3" />
                  </Button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

